import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, ExternalLink, KeyRound, ShieldCheck, ShieldOff, Ban, RotateCcw, Zap, Copy } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LicenseStatusBadge, PlanBadge, EntitlementBadge, formatQuota } from "./LicenseBadges";

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; } }

function Row({ label, value, mono = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-white/5 last:border-0">
      <div className="text-[10px] text-white/50 uppercase tracking-wider">{label}</div>
      <div className={`text-xs text-white text-right min-w-0 truncate ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function Section({ title, icon: Icon, testid, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon ? <Icon size={14} className="text-emerald-300" /> : null}
        <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">{title}</div>
      </div>
      {children}
    </div>
  );
}

function QuotaCell({ label, used, limit, testid }) {
  const isUnlimited = limit === -1;
  const pct = isUnlimited ? 0 : Math.min(100, Math.round(((used || 0) / Math.max(1, limit)) * 100));
  const barColor = pct > 90 ? "bg-rose-500" : pct > 70 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</div>
        <div className="text-[10px] font-mono text-white/70">{used || 0} / {formatQuota(limit)}</div>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full ${isUnlimited ? "bg-gradient-to-r from-emerald-400 to-purple-500" : barColor}`} style={{ width: isUnlimited ? "100%" : `${pct}%`, transition: "width 400ms" }} />
      </div>
    </div>
  );
}

export default function LicenseDetail() {
  const { tenantId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewDur, setRenewDur] = useState("30");
  const [renewPlan, setRenewPlan] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, h] = await Promise.all([
        api.get(`/platform/licenses/${tenantId}`),
        api.get(`/platform/licenses/${tenantId}/history`),
      ]);
      setData(d.data);
      setHistory(h.data.entries || []);
    } catch (e) { notifyError(e); setData(null); setHistory([]); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (action, extra = {}) => {
    try {
      await api.post(`/platform/licenses/${tenantId}/${action}`, extra);
      toast.success(`License ${action}.`);
      load();
    } catch (e) { notifyError(e, `${action} gagal`); }
  };

  const renew = async () => {
    const body = { duration_days: parseInt(renewDur, 10) };
    if (renewPlan) body.plan_code = renewPlan;
    doAction("renew", body);
  };
  const suspend = () => {
    const reason = window.prompt("Alasan suspend?");
    if (reason && reason.length >= 3) doAction("suspend", { reason });
  };
  const revoke = () => {
    const reason = window.prompt("Alasan revoke? Aksi ini bersifat permanen.");
    if (reason && reason.length >= 3) doAction("revoke", { reason });
  };
  const copyKey = () => {
    if (!data?.license?.license_key) return;
    navigator.clipboard.writeText(data.license.license_key);
    toast.success("License key disalin.");
  };

  if (loading) return (
    <PlatformLayout title="License Detail"><div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center" data-testid="ldt-loading">Memuat…</div></PlatformLayout>
  );

  if (!data) return (
    <PlatformLayout title="License Detail">
      <div className="p-8 rounded-2xl border border-white/10 text-center space-y-3" data-testid="ldt-not-found">
        <div className="text-white/70 text-sm">License tidak ditemukan untuk tenant ini.</div>
        <Button variant="ghost" onClick={() => nav("/platform/licenses")} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="ldt-not-found-back">
          <ArrowLeft size={14} className="mr-1" /> Kembali
        </Button>
      </div>
    </PlatformLayout>
  );

  const lic = data.license || {};
  const tenant = data.tenant || {};
  const sub = data.subscription || {};
  const acct = data.billing_account || {};
  const ent = lic.entitlements || {};
  const usage = lic.usage || {};
  const quota = ent.quota || {};
  const features = ent.features || {};
  const limits = ent.limits || {};

  return (
    <PlatformLayout title={tenant.tenant_name || "License Detail"}>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Button variant="ghost" onClick={() => nav("/platform/licenses")} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="ldt-back">
          <ArrowLeft size={14} className="mr-1" /> Kembali
        </Button>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="ldt-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button variant="ghost" onClick={() => doAction("validate", {})} className="text-sky-300 hover:bg-sky-500/10 border border-sky-500/40" data-testid="ldt-validate">
            <ShieldCheck size={14} className="mr-1" /> Validate
          </Button>
          {lic.status !== "ACTIVE" && lic.status !== "REVOKED" && (
            <Button variant="ghost" onClick={() => doAction("activate", { note: "" })} className="text-emerald-300 hover:bg-emerald-500/10 border border-emerald-500/40" data-testid="ldt-activate">
              <ShieldCheck size={14} className="mr-1" /> Activate
            </Button>
          )}
          {lic.status !== "SUSPENDED" && lic.status !== "REVOKED" && (
            <Button variant="ghost" onClick={suspend} className="text-amber-300 hover:bg-amber-500/10 border border-amber-500/40" data-testid="ldt-suspend">
              <ShieldOff size={14} className="mr-1" /> Suspend
            </Button>
          )}
          {lic.status !== "REVOKED" && (
            <Button variant="ghost" onClick={revoke} className="text-rose-300 hover:bg-rose-500/10 border border-rose-500/40" data-testid="ldt-revoke">
              <Ban size={14} className="mr-1" /> Revoke
            </Button>
          )}
          <Link to={`/platform/billing/tenant/${tenantId}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-purple-500/40 text-purple-300 hover:bg-purple-500/10 text-xs font-bold" data-testid="ldt-open-billing">
            <ExternalLink size={12} /> Billing
          </Link>
        </div>
      </div>

      {/* Overview */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mb-5" data-testid="ldt-overview">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          <div className="text-white font-display font-black text-lg truncate" data-testid="ldt-tenant-name">{tenant.tenant_name}</div>
          <PlanBadge plan={lic.plan_code} />
          <LicenseStatusBadge status={lic.status} />
          <span className="text-[10px] text-white/40 font-mono">· {tenantId}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-black/30 px-3 py-1.5" data-testid="ldt-license-key">
            <KeyRound size={12} className="text-emerald-300" />
            <code className="font-mono text-xs text-white/90">{lic.license_key}</code>
            <button onClick={copyKey} className="ml-1 text-white/40 hover:text-white" title="Copy" data-testid="ldt-copy-key">
              <Copy size={11} />
            </button>
          </div>
          <div className="text-[10px] text-white/40">
            Issued: {fmt(lic.issued_at)} · Expires: <span className="text-white/80">{fmt(lic.expires_at)}</span>
            {lic.grace_until ? ` · Grace: ${fmt(lic.grace_until)}` : ""}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Subscription */}
        <Section title="Subscription" icon={Zap} testid="ldt-section-subscription">
          <Row label="Plan" value={sub.plan_code || sub.package} mono />
          <Row label="Type" value={sub.subscription_type} mono />
          <Row label="Status" value={sub.status} />
          <Row label="Start" value={fmt(sub.start_at || sub.started_at)} />
          <Row label="End" value={fmt(sub.end_at || sub.ends_at)} />
        </Section>

        {/* License */}
        <Section title="License" icon={KeyRound} testid="ldt-section-license">
          <Row label="Plan" value={lic.plan_code} mono />
          <Row label="Status" value={lic.status} />
          <Row label="Issued" value={fmt(lic.issued_at)} />
          <Row label="Activated" value={fmt(lic.activated_at)} />
          <Row label="Expires" value={fmt(lic.expires_at)} />
          <Row label="Last Validation" value={fmt(lic.last_validation)} />
          <Row label="Billing Account" value={acct.status || "—"} />
        </Section>

        {/* Entitlements */}
        <Section title="Entitlements (Features)" icon={ShieldCheck} testid="ldt-section-entitlements">
          <div className="flex flex-wrap gap-1.5">
            <EntitlementBadge enabled={features.api_access} label="API" testid="ldt-ent-api" />
            <EntitlementBadge enabled={features.ai_access} label="AI" testid="ldt-ent-ai" />
            <EntitlementBadge enabled={features.white_label} label="White Label" testid="ldt-ent-white-label" />
            <EntitlementBadge enabled={features.branding_custom} label="Branding" testid="ldt-ent-branding" />
            <EntitlementBadge enabled={features.sso} label="SSO" testid="ldt-ent-sso" />
            <EntitlementBadge enabled={features.webhook} label="Webhook" testid="ldt-ent-webhook" />
            <EntitlementBadge enabled={features.custom_domain} label="Custom Domain" testid="ldt-ent-domain" />
            <EntitlementBadge enabled={features.email_notification} label="Email" testid="ldt-ent-email" />
            <EntitlementBadge enabled={features.priority_support} label="Priority" testid="ldt-ent-priority" />
          </div>
          <div className="mt-3 pt-3 border-t border-white/5 text-[10px] text-white/40">
            Limits: max_products = <span className="text-white/80 font-mono">{formatQuota(limits.max_products)}</span> · max_orders/mo = <span className="text-white/80 font-mono">{formatQuota(limits.max_orders_per_month)}</span>
          </div>
        </Section>

        {/* Renew */}
        <Section title="Renew License" icon={RotateCcw} testid="ldt-section-renew">
          <div className="grid grid-cols-2 gap-2 mb-2">
            <Input value={renewDur} onChange={(e) => setRenewDur(e.target.value)} type="number" min="1" max="3650"
              className="bg-white/5 border-white/10 text-white text-xs" data-testid="ldt-renew-duration" />
            <Select value={renewPlan || "SAME"} onValueChange={(v) => setRenewPlan(v === "SAME" ? "" : v)}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white text-xs" data-testid="ldt-renew-plan"><SelectValue placeholder="Plan (opt)" /></SelectTrigger>
              <SelectContent className="z-[110] bg-[#0F0F17] border-white/10 text-white">
                {["SAME", "STARTER", "GROWTH", "ENTERPRISE"].map((v) => (
                  <SelectItem key={v} value={v} className="text-white focus:bg-white/10 focus:text-white">{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={renew} className="w-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 text-xs" data-testid="ldt-renew-submit">
            <RotateCcw size={12} className="mr-1" /> Renew License
          </Button>
        </Section>

        {/* Quota */}
        <Section title="Quota & Usage" icon={ShieldCheck} testid="ldt-section-quota">
          <div className="grid grid-cols-2 gap-2">
            <QuotaCell label="Website" used={usage.website} limit={quota.website} testid="ldt-quota-website" />
            <QuotaCell label="Admin" used={usage.admin} limit={quota.admin} testid="ldt-quota-admin" />
            <QuotaCell label="Storage (GB)" used={usage.storage_gb} limit={quota.storage_gb} testid="ldt-quota-storage" />
            <QuotaCell label="Deployment" used={usage.deployment} limit={quota.deployment} testid="ldt-quota-deployment" />
            <QuotaCell label="API/day" used={usage.api_request_today} limit={quota.api_request_per_day} testid="ldt-quota-api" />
            <QuotaCell label="AI/day" used={usage.ai_request_today} limit={quota.ai_request_per_day} testid="ldt-quota-ai" />
          </div>
        </Section>

        {/* History */}
        <Section title="License History" icon={KeyRound} testid="ldt-section-history">
          {history.length === 0 ? (
            <div className="text-white/40 text-xs italic py-2" data-testid="ldt-history-empty">Belum ada history.</div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {history.map((h) => (
                <div key={h.id} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0 text-xs" data-testid={`ldt-history-${h.id}`}>
                  <span className="font-mono text-white truncate">{h.event}</span>
                  <span className="text-[10px] text-white/60 ml-2">{h.status_after || "—"}</span>
                  <span className="text-[10px] text-white/40 font-mono ml-auto">{fmt(h.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </PlatformLayout>
  );
}
